/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Modal,
} from "react-native";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db, functions } from "../firebase.config";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import AuthHeader from "../components/AuthHeader";
import PinAuthLayer from "../components/PinAuthLayer";
import { useAuthStore } from "../stores";
import { isDeviceTrusted } from "../utils/deviceTrust";
import { formatPhoneNumber, formatCentsAsUSD, capitalizeWords } from "../utils/formatting";

const CancelBookingConfirmation = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [shopInfo, setShopInfo] = useState({});
  const [storeLogo, setStoreLogo] = useState("");

  // Check authentication - handled by store
  useEffect(() => {
    if (isAuthenticated) {
        setIsVerified(true); // Authenticated users bypass verification
        loadBookingData();
    } else {
        setLoading(false); // Show PIN screen
    }
  }, [isAuthenticated]);

  const handleVerified = async () => {
    console.log("✅ PIN verified for cancellation");
    setIsVerified(true);
    await loadBookingData();
  };

  const loadBookingData = async () => {
    try {
      setLoading(true);
      
      if (!bookingId) {
        alert("Error: No booking ID provided");
        navigate("/");
        return;
      }
      
      // Try multi-tenant path first
      let bookingDoc = await getDoc(doc(db, "tenants/1234/stores/999/bookings", bookingId));
      
      if (!bookingDoc.exists()) {
        // Try root path
        bookingDoc = await getDoc(doc(db, "bookings", bookingId));
      }
      
      if (!bookingDoc.exists()) {
        alert("Error: Booking not found");
        navigate("/");
        return;
      }
      
      const data = bookingDoc.data();
      setBookingData(data);
      
      // Load store info
      const settingsPath = "tenants/1234/stores/999/settings";
      const storeNameDoc = await getDoc(doc(db, settingsPath, "storeName"));
      const storeEmailDoc = await getDoc(doc(db, settingsPath, "storeEmail"));
      const storePhoneDoc = await getDoc(doc(db, settingsPath, "storePhone"));
      const storeLogoDoc = await getDoc(doc(db, settingsPath, "storeLogo"));
      
      setShopInfo({
        name: storeNameDoc.exists() ? storeNameDoc.data().name : "",
        email: storeEmailDoc.exists() ? storeEmailDoc.data().email : "",
        phone: storePhoneDoc.exists() ? storePhoneDoc.data().phone : "",
      });
      
      setStoreLogo(storeLogoDoc.exists() ? storeLogoDoc.data().url : "");
      
    } catch (error) {
      console.error("❌ Error loading booking:", error);
      alert("Error: Failed to load booking");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCancellation = async () => {
    const firstName = capitalizeWords(bookingData.firstName || "");
    const lastName = capitalizeWords(bookingData.lastName || "");
    const customerName = `${firstName} ${lastName}`.trim();
    const totalPaidCents = bookingData.payments?.[0]?.costBreakdown?.totalCharged || 0;
    const totalPaid = formatCentsAsUSD(totalPaidCents);
    
    if (!window.confirm(
      `⚠️ CONFIRM BOOKING CANCELLATION?\n\n` +
      `Customer: ${customerName}\n` +
      `Booking ID: ${bookingId}\n` +
      `Total Paid: ${totalPaid}\n\n` +
      `This will:\n` +
      `• Cancel the booking\n` +
      `• Issue refund based on cancellation policy\n` +
      `• Remove the booking from the system\n` +
      `• Send confirmation email & SMS to customer\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Proceed with cancellation?`
    )) {
      return;
    }
    
    try {
      setProcessing(true);
      console.log("🚫 Processing cancellation:", bookingId);
      console.log("🔍 Functions instance:", functions);
      
      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }
      
      const cancelBooking = httpsCallable(functions, "onCustomerCancellation");
      
      const result = await cancelBooking({
        bookingId: bookingId,
      });
      
      console.log("✅ Booking cancelled successfully:", result.data);
      
      const refundAmount = result.data.totalRefunded 
        ? formatCentsAsUSD(result.data.totalRefunded) 
        : totalPaid;
      alert(
        `✅ Booking Cancelled Successfully!\n\n` +
        `Refund Amount: ${refundAmount}\n` +
        `Refund Percentage: ${result.data.refundPercentage || 100}%\n\n` +
        `Customer has been notified via email and SMS.`
      );
      
      // Navigate to home
      navigate("/");
    } catch (error) {
      console.error("❌ Error cancelling booking:", error);
      alert(`Error: Failed to cancel booking - ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Show verification layer if not authenticated
  if (!isVerified && !loading && bookingId) {
    return (
      <View style={styles.container}>
        <AuthHeader 
          title="Cancel Booking"
          subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <PinAuthLayer 
          onAuthenticated={handleVerified}
          bookingId={bookingId}
        />
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <AuthHeader 
          title="Cancel Booking"
          subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color="#C4453D" />
          <Text style={styles.loadingText}>Loading cancellation details...</Text>
        </View>
      </View>
    );
  }

  if (!bookingData) {
    return (
      <View style={styles.container}>
        <AuthHeader 
          title="Cancel Booking"
          subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <View style={styles.centerContent}>
          <Text style={styles.errorText}>Booking not found</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={() => navigate("/")}>
            <Text style={styles.buttonText}>Go Home</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Format customer name (capitalize first letters)
  const firstName = capitalizeWords(bookingData.firstName || "");
  const lastName = capitalizeWords(bookingData.lastName || "");
  const customerName = `${firstName} ${lastName}`.trim();
  
  // Format total paid from cents to dollars
  const totalPaidCents = bookingData.payments?.[0]?.costBreakdown?.totalCharged || 0;
  const totalPaid = formatCentsAsUSD(totalPaidCents);
  
  // Format phone number
  const formattedPhone = bookingData.customerPhone ? formatPhoneNumber(bookingData.customerPhone) : "N/A";
  
  // Get card info for refund display
  const cardInfo = bookingData.payments?.[0]?.cardInfo;
  const cardBrand = cardInfo?.brand ? cardInfo.brand.charAt(0).toUpperCase() + cardInfo.brand.slice(1) : null;
  const cardLast4 = cardInfo?.last4 || null;
  const cardDisplay = cardBrand && cardLast4 ? `${cardBrand} •••• ${cardLast4}` : null;
  
  // Calculate estimated refund based on cancellation policy
  let estimatedRefundPercent = 100; // Default to 100%
  
  // Try to calculate refund percentage based on start date
  if (bookingData.startDate) {
    const now = new Date();
    let startDate;
    
    // Handle Firestore Timestamp
    if (bookingData.startDate?.toDate) {
      startDate = bookingData.startDate.toDate();
    } else if (bookingData.startDate?.seconds) {
      startDate = new Date(bookingData.startDate.seconds * 1000);
    } else {
      startDate = new Date(bookingData.startDate);
    }
    
    // Set start date to 11 AM for comparison (matching Cloud Function logic)
    const startDateAt11AM = new Date(startDate);
    startDateAt11AM.setHours(11, 0, 0, 0);
    
    const hoursUntilRental = (startDateAt11AM - now) / (1000 * 60 * 60);
    
    // Apply default cancellation policy (matching Cloud Function defaults)
    if (hoursUntilRental >= 48) {
      estimatedRefundPercent = 100; // 100% refund
    } else if (hoursUntilRental >= 24) {
      estimatedRefundPercent = 75; // 75% refund
    } else {
      estimatedRefundPercent = 50; // 50% refund
    }
  }
  
  // Calculate refund amount: totalPaid * refundPercent / 100
  const estimatedRefundCents = Math.round(totalPaidCents * (estimatedRefundPercent / 100));
  const estimatedRefund = formatCentsAsUSD(estimatedRefundCents);

  return (
    <View style={styles.container}>
      <AuthHeader 
        title="Cancel Booking"
        subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      <ScrollView style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerIcon}>🚫</Text>
          <Text style={styles.headerTitle}>Cancel Booking</Text>
        </View>

        {/* Warning Card */}
        <View style={styles.warningCard}>
          <Text style={styles.warningIcon}>⚠️</Text>
          <Text style={styles.warningTitle}>Important: This action cannot be undone</Text>
          <Text style={styles.warningText}>
            Cancelling this booking will remove it from the system and issue a refund according to our cancellation policy.
          </Text>
        </View>

        {/* Booking Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>📋 Cancellation Summary</Text>
          
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Booking ID:</Text>
            <Text style={styles.summaryValue}>{bookingData.customerBookingID || bookingId}</Text>
          </View>
          
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Customer:</Text>
            <Text style={styles.summaryValue}>{customerName}</Text>
          </View>
          
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Email:</Text>
            <Text style={styles.summaryValue}>{bookingData.customerEmail || "N/A"}</Text>
          </View>
          
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Phone:</Text>
            <Text style={styles.summaryValue}>{formattedPhone}</Text>
          </View>
          
          <View style={[styles.summaryRow, styles.summaryRowTotal]}>
            <Text style={styles.summaryLabelTotal}>Total Paid:</Text>
            <Text style={styles.summaryValueTotal}>{totalPaid}</Text>
          </View>
        </View>

        {/* Refund Policy Notice */}
        <View style={styles.policyCard}>
          <Text style={styles.policyTitle}>💰 Refund Policy</Text>
          <Text style={styles.policyText}>
            The refund amount will be calculated based on our cancellation policy:
          </Text>
          <Text style={styles.policyBullet}>• 48+ hours before rental: 100% refund</Text>
          <Text style={styles.policyBullet}>• 24-48 hours before rental: 75% refund</Text>
          <Text style={styles.policyBullet}>• Less than 24 hours: 50% refund</Text>
          {(cardDisplay || estimatedRefund) && (
            <View style={styles.refundInfoContainer}>
              {estimatedRefund && (
                <>
                  <Text style={styles.refundInfoLabel}>Refund Amount:</Text>
                  <Text style={styles.refundInfoValue}>{estimatedRefund}</Text>
                </>
              )}
              {cardDisplay && (
                <>
                  <Text style={[styles.refundInfoLabel, { marginTop: estimatedRefund ? 12 : 0 }]}>
                    Refund will be processed to:
                  </Text>
                  <Text style={styles.refundInfoValue}>{cardDisplay}</Text>
                </>
              )}
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionsContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.actionButton, styles.cancelActionButton]}
            onPress={() => navigate(-1)}
            disabled={processing}
          >
            <Text style={styles.actionButtonText}>← Go Back</Text>
          </TouchableOpacity>
          
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.actionButton, styles.confirmActionButton]}
            onPress={handleConfirmCancellation}
            disabled={processing}
          >
            {processing ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.actionButtonText}>✓ Confirm Cancellation</Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Help Text */}
        <View style={styles.helpContainer}>
          <Text style={styles.helpText}>
            Need assistance? Contact us at {shopInfo.phone || shopInfo.email}
          </Text>
        </View>
      </ScrollView>

      {/* Processing Modal */}
      <Modal
        visible={processing}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#C4453D" />
            <Text style={styles.modalText}>Processing cancellation...</Text>
            <Text style={styles.modalSubtext}>Please wait while we process your request.</Text>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  content: {
    flex: 1,
    padding: 20,
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  centerContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    marginTop: 20,
    fontSize: 16,
    color: "#5C6B68",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    textAlign: "center",
    marginBottom: 20,
  },
  header: {
    backgroundColor: "#C4453D",
    padding: 30,
    alignItems: "center",
  },
  headerIcon: {
    fontSize: 48,
    marginBottom: 10,
  },
  headerTitle: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#FFFFFF",
    opacity: 0.9,
  },
  warningCard: {
    backgroundColor: "#FDF3E3",
    margin: 20,
    padding: 20,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    alignItems: "center",
  },
  warningIcon: {
    fontSize: 36,
    marginBottom: 10,
  },
  warningTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 10,
    textAlign: "center",
  },
  warningText: {
    fontSize: 14,
    color: "#5C6B68",
    textAlign: "center",
    lineHeight: 20,
  },
  summaryCard: {
    backgroundColor: "#FFFFFF",
    margin: 20,
    padding: 20,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#DDE3E2",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  summaryLabel: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  summaryValue: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "600",
  },
  summaryRowTotal: {
    borderBottomWidth: 0,
    borderTopWidth: 2,
    borderTopColor: "#C4453D",
    marginTop: 10,
    paddingTop: 15,
  },
  summaryLabelTotal: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "bold",
  },
  summaryValueTotal: {
    fontSize: 18,
    color: "#C4453D",
    fontWeight: "bold",
  },
  policyCard: {
    backgroundColor: "#E3F2EF",
    margin: 20,
    padding: 20,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  policyTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 10,
  },
  policyText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
    marginBottom: 8,
  },
  policyBullet: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 22,
    marginLeft: 10,
  },
  refundInfoContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E3F2EF",
  },
  refundInfoLabel: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "600",
    marginBottom: 6,
  },
  refundInfoValue: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "bold",
  },
  actionsContainer: {
    flexDirection: "row",
    padding: 20,
    gap: 15,
  },
  actionButton: {
    flex: 1,
    padding: 18,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 55,
  },
  cancelActionButton: {
    backgroundColor: "#5C6B68",
  },
  confirmActionButton: {
    backgroundColor: "#C4453D",
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  helpContainer: {
    padding: 20,
    alignItems: "center",
  },
  helpText: {
    fontSize: 13,
    color: "#5C6B68",
    textAlign: "center",
    fontStyle: "italic",
  },
  button: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    minWidth: 150,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 30,
    alignItems: "center",
    minWidth: 280,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  modalText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    marginTop: 20,
    textAlign: "center",
  },
  modalSubtext: {
    fontSize: 14,
    color: "#5C6B68",
    marginTop: 8,
    textAlign: "center",
  },
});

export default CancelBookingConfirmation;

